import { Calculation } from '@servicetitan/financing-api-client';
import { PreApprovalDto } from '@servicetitan/financing-api-client/src/prequals-api';
import { SetupDataStore } from '@servicetitan/financing-workflow-shared';
import {
    isGreenSkyProvider,
    isRenderableGreenSkyPreApproval,
    getGeneralDisclosureText,
} from '@servicetitan/financing-utils-shared';
import { FinancedAmountStore } from '../financing-information/financedAmountStore';
import { useLoanAmountValidation } from '../providers/useLoanAmountValidation';
import { usePreApprovalCalculation } from './hooks';
import { PreApprovalStore } from './preApprovalStore';

/**
 * The single pre-approval-section decision that `Container` consumes to compose every
 * pre-approval-affected slot it hands to `PlansList` (the card, the out-of-range notice, the
 * selector title/visibility, and the inline disclosure). `visible`/`outOfRange` always carry a
 * non-null `Calculation`, so the type system rules out rendering `PlanCard` without one. `loading`
 * carries the `PreApproval` (its calc is still in flight) so `Container` can exclude the pre-approved
 * plan from the selector before the card resolves.
 */
export type PreApprovalDisplay =
    | { kind: 'hidden' }
    | { kind: 'loading'; preApproval: PreApprovalDto }
    | {
          kind: 'visible';
          preApproval: PreApprovalDto;
          calculation: Calculation;
          generalDisclosure: string;
      }
    | {
          kind: 'outOfRange';
          preApproval: PreApprovalDto;
          calculation: Calculation;
          generalDisclosure: string;
      };

export const usePreApprovalDisplay = (): PreApprovalDisplay => {
    const { preApproval } = PreApprovalStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const { financedAmount } = FinancedAmountStore.useStore();
    const { isValidLoanAmount } = useLoanAmountValidation(financedAmount);

    // Gates, in order. Any failure resolves to `hidden`; together they gate the targeted calc so
    // non-opted-in consumers and non-GreenSky/non-Approved/no-planNumber records fire no fetch.
    const gate1 = preApproval != null; // opt-in store value present
    const gate2 = !setupData?.prequalificationId && !setupData?.loanId; // not a prequal/loan flow
    const gate3 = isGreenSkyProvider(setupData?.provider); // currently displayed provider
    const gate4 = isRenderableGreenSkyPreApproval(preApproval) && !!preApproval?.planNumber;
    const gate5 = Number.isFinite(financedAmount) && financedAmount > 0; // positive-finite sanity
    const gate6 = isValidLoanAmount === true; // page-level provider-bound validation

    const calcEnabled = gate1 && gate2 && gate3 && gate4 && gate5 && gate6;

    const calc = usePreApprovalCalculation({
        preApprovalId: preApproval?.id,
        planNumber: preApproval?.planNumber,
        amount: financedAmount,
        tenantId: setupData?.tenantId ?? null,
        buId: setupData?.buId ?? null,
        state: setupData?.state,
        enabled: calcEnabled,
    });

    if (!calcEnabled) {
        // Covers gates 1–6 failing (including an out-of-bounds amount); no fetch fired.
        return { kind: 'hidden' };
    }

    // calcEnabled implies a renderable GreenSky pre-approval with a non-null amount and plan number.
    const pa = preApproval as PreApprovalDto;
    const amount = pa.amount as number;
    const generalDisclosure = getGeneralDisclosureText(pa);

    if (calc.isError) {
        return { kind: 'hidden' };
    }

    const row = calc.data?.find(c => c.planNumber === pa.planNumber) ?? null;

    if ((calc.isLoading || calc.isFetching) && !row) {
        return { kind: 'loading', preApproval: pa };
    }

    if (row === null) {
        // 200 with no row for the plan number (plan absent from GreenSky's catalog) — like a 404.
        return { kind: 'hidden' };
    }

    // The out-of-range notice reads "between {min}–{max}", where max is the effective upper bound:
    // the pre-approval limit, capped by the plan's maxLoanAmount when GreenSky sets it (0 = unset).
    // A degenerate range — minimum above that upper bound (e.g. a positive maxLoanAmount below
    // minLoanAmount) — can't be described honestly, so treat the pre-approval as absent.
    const upperBound = Math.min(amount, row.maxLoanAmount || Infinity);
    if (row.minLoanAmount > upperBound) {
        return { kind: 'hidden' };
    }

    if (row.eligibleForApplication === true && financedAmount <= amount) {
        return { kind: 'visible', preApproval: pa, calculation: row, generalDisclosure };
    }

    return { kind: 'outOfRange', preApproval: pa, calculation: row, generalDisclosure };
};
